/**
 * Scrollable message list — scrolls the pane, not the window (planning pattern).
 * Assistant messages may include choice chips (e.g. Plan A / Plan B).
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import type { ConversationMessage } from '@/types/aiStudio';
import { avatarUrlForUser, ChatUserAvatar } from './ChatUserAvatar';
import { stripAssistantDisplayContent } from './stripAssistantDisplayContent';
import { stripOrphanCitationMarkers } from './stripOrphanCitationMarkers';

interface ConversationMessageListProps {
  messages: ConversationMessage[];
  sending?: boolean;
  emptyStateText?: string;
  className?: string;
  /** Planning workspace: user right + avatar, assistant full width, prose-only JSON strip. */
  workspaceLayout?: boolean;
  /** When user clicks a choice chip, send that text as the next turn. */
  onChoiceSelect?: (sendText: string) => void;
  onUndoTurn?: (userMessageId: string) => void;
  undoBusy?: boolean;
}

function displayContent(message: ConversationMessage, workspaceLayout: boolean): string {
  if (message.role === 'assistant') {
    const stripped = workspaceLayout
      ? stripAssistantDisplayContent(message.content)
      : stripOrphanCitationMarkers(message.content);
    return stripped || stripOrphanCitationMarkers(message.content);
  }
  return message.content;
}

export const ConversationMessageList: React.FC<ConversationMessageListProps> = ({
  messages,
  sending = false,
  emptyStateText,
  className = '',
  workspaceLayout = false,
  onChoiceSelect,
  onUndoTurn,
  undoBusy = false,
}) => {
  const { t, translateWithFallback } = useLanguage();
  const { user } = useAuth();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const userDisplayName =
    user && `${user.firstName || ''} ${user.lastName || ''}`.trim()
      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
      : user?.email ?? null;

  /** Scroll only the messages pane — never the outer page (Learning Area 43). */
  const jumpToBottom = useCallback(() => {
    const run = () => {
      const scroller = scrollRef.current;
      if (scroller) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    };
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  useEffect(() => {
    jumpToBottom();
  }, [messages, sending, jumpToBottom]);

  const awaitingResponse = sending && messages.some((message) => message.pending);
  const lastAssistantWithChoices = [...messages]
    .reverse()
    .find((m) => m.role === 'assistant' && m.choices && m.choices.length > 0 && !m.pending);

  return (
    <div
      ref={scrollRef}
      tabIndex={0}
      role="log"
      aria-label={t('aiStudio.conversation.messageList')}
      className={`min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-y-contain px-4 py-3 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-info)] ${className}`}
    >
      {messages.length === 0 && !sending && emptyStateText && (
        <p className="text-base text-[var(--color-surface-text-secondary)]">{emptyStateText}</p>
      )}

      {messages.map((message) => {
        const text = displayContent(message, workspaceLayout);
        const isUser = message.role === 'user';

        return (
          <div
            key={message.id}
            className={`flex w-full items-start gap-2 ${
              workspaceLayout && isUser ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`${
                workspaceLayout && message.role === 'assistant'
                  ? 'w-full max-w-full px-2 py-1'
                  : 'max-w-[85%] px-4 py-2'
              } ${
                isUser
                  ? `relative rounded-[1.25rem] border-2 border-[var(--color-border)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] shadow-sm ${
                      onUndoTurn ? 'pb-6 pr-7' : ''
                    }`
                  : 'rounded-lg bg-transparent text-[var(--color-surface-text)]'
              }`}
              data-message-role={message.role}
            >
              <span className="sr-only">
                {isUser
                  ? t('aiStudio.conversation.roleUser')
                  : t('aiStudio.conversation.roleAssistant')}
              </span>
              {isUser && message.authorName ? (
                <div className="mb-0.5 text-[10px] leading-tight text-[var(--color-surface-text-secondary)]">
                  {message.authorName}
                </div>
              ) : null}
              {text ? (
                <div className="text-base leading-relaxed whitespace-pre-wrap">{text}</div>
              ) : message.pending ? null : (
                <div className="text-base italic text-[var(--color-surface-text-secondary)]">
                  {t('aiStudio.authoring.appliedToBlueprint')}
                </div>
              )}
              {isUser && onUndoTurn && !message.pending && (
                <button
                  type="button"
                  onClick={() => onUndoTurn(message.id)}
                  disabled={undoBusy || sending}
                  className="absolute bottom-1 right-1 rounded p-0.5 text-[var(--color-surface-text-secondary)] hover:text-[var(--color-surface-text)] disabled:opacity-40"
                  title={translateWithFallback(
                    'operations.pane.undoTurn',
                    'Undo this change and everything after'
                  )}
                  aria-label={translateWithFallback(
                    'operations.pane.undoTurn',
                    'Undo this change and everything after'
                  )}
                >
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                </button>
              )}
              {message.id === lastAssistantWithChoices?.id &&
                message.choices &&
                message.choices.length > 0 &&
                onChoiceSelect &&
                !sending && (
                  <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Plan choices">
                    {message.choices.map((choice) => (
                      <button
                        key={choice.id}
                        type="button"
                        onClick={() => onChoiceSelect(choice.sendText)}
                        className="rounded-lg border border-[var(--color-border)] bg-[var(--color-primary-bg)] px-3 py-1.5 text-base font-medium text-[var(--color-primary-text)] hover:opacity-90"
                      >
                        {choice.label}
                      </button>
                    ))}
                  </div>
                )}
            </div>
            {workspaceLayout && isUser && (
              <ChatUserAvatar
                displayName={message.authorName || userDisplayName}
                avatarUrl={
                  !message.authorName || message.authorName === userDisplayName
                    ? avatarUrlForUser(user)
                    : null
                }
              />
            )}
          </div>
        );
      })}

      {awaitingResponse && (
        <div className="flex justify-start">
          <div className="rounded-xl border border-[var(--color-border)] bg-transparent px-4 py-2 text-base text-[var(--color-surface-text)]">
            {t('aiStudio.conversation.thinking')}
          </div>
        </div>
      )}
    </div>
  );
};
